import React, { useCallback, useEffect, useState } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Landmark, ArrowDownToLine, ArrowUpFromLine, ExternalLink } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { DeskMintForm, DeskRedeemForm, money } from '../../components/pas/DeskForms';
import { SwapStatus } from '../../components/pas/SwapForm';
import { CreditCashoutCard } from '../../components/pas/CreditCashout';
import { CreditToBethCard } from '../../components/pas/CreditToBeth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-desk`;

const Card = ({ icon, title, sub, children }) => (
  <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
    <h2 className="font-semibold flex items-center gap-2 mb-1">{icon} {title}</h2>
    <p className="text-xs text-slate-400 mb-4">{sub}</p>
    {children}
  </div>
);

const RequestRow = ({ r }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-800 text-sm" data-testid={`desk-request-${r.id}`}>
    <span>{r.kind === 'mint' ? `Buy ${money(r.gross)} → ${r.net} BETH` : r.kind === 'credit_cashout' ? `Credit cash-out ${money(r.gross)} → ${money(r.net)}` : `Cash out ${r.gross} BETH → ${money(r.net)}`} · {r.network}</span>
    <span className="flex items-center gap-2"><SwapStatus s={r.status} />
      {r.tx && <a href={r.tx} target="_blank" rel="noreferrer" className="text-emerald-400"><ExternalLink className="w-3 h-3" /></a>}</span>
    {(r.error || r.reason) && <p className="w-full text-xs text-red-400">{r.error || r.reason}</p>}
  </div>
);

export default function PASDesk() {
  const { token } = usePASClientAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/info`, h).then(r => setInfo(r.data)).catch(() => {});
    axios.get(`${API}/requests`, h).then(r => setReqs(r.data.requests)).catch(() => {});
  }, [token]);
  useEffect(() => { if (!token) navigate('/pas/login?next=/pas/desk'); else load(); }, [token, navigate, load]);

  const submit = async (kind, body) => {
    setBusy(kind);
    try { const r = await axios.post(`${API}/${kind}`, body, auth); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Request failed'); }
    setBusy('');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8" data-testid="desk-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <button onClick={() => navigate('/pas/dashboard')} className="text-slate-400 text-sm flex items-center gap-1" data-testid="desk-back-btn"><ArrowLeft className="w-4 h-4" /> Dashboard</button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Landmark className="w-8 h-8 text-emerald-400" /> Mint &amp; Redeem Desk</h1>
        </div>
        <SectionGuide id="desk" />
        <div className="grid md:grid-cols-2 gap-6">
          <Card icon={<ArrowDownToLine className="w-5 h-5 text-emerald-400" />} title="Buy BETH USD" sub="Pay dollars (USDC or bank), receive BETH USD in your wallet once payment is confirmed.">
            <DeskMintForm info={info} busy={busy === 'mint'} onSubmit={submit} />
          </Card>
          <Card icon={<ArrowUpFromLine className="w-5 h-5 text-amber-400" />} title="Cash out BETH USD" sub="Your BETH is held in escrow on approval, then burned when the admin sends your dollars.">
            <DeskRedeemForm info={info} busy={busy === 'redeem'} onSubmit={submit} />
          </Card>
        </div>
        <CreditToBethCard token={token} onDone={load} />
        <CreditCashoutCard token={token} onRequested={load} />
        <div data-testid="desk-requests">
          <p className="text-sm text-slate-300 font-medium mb-2">My desk requests</p>
          {!reqs.length ? <p className="text-xs text-slate-500">None yet.</p> : reqs.map(r => <RequestRow key={r.id} r={r} />)}
        </div>
      </div>
    </div>
  );
}
